import { DeleteIcon } from "@/components/common/icons/DeleteIcon/DeleteIcon"
import { LoadingSpinner } from "@/components/common/LoadingSpinner/LoadingSpinner"
import { Modal } from "@/components/common/Modal/Modal"
import { toast } from "@/components/common/Toast"
import { useTranslation } from "@/hooks/useTranslation/useTranslation"
import { useDeleteChatConversation } from "@/services/api/chatConversationService"
import { useEffect, useRef } from "react"
import { createPortal } from "react-dom"

interface DeleteConversationModalProps {
  conversationId: string
  setIsDeleteModalOpen: (isOpen: boolean) => void
  dataTestId?: string
}

export const DeleteConversationModal: React.FC<
  DeleteConversationModalProps
> = ({ conversationId, setIsDeleteModalOpen, dataTestId }) => {
  const { translate } = useTranslation()
  const isProcessingRef = useRef(false)

  const {
    mutate: deleteConversation,
    isPending: isDeletePending,
    isSuccess: isDeleteSuccess,
    isError: isDeleteError,
  } = useDeleteChatConversation()

  useEffect(() => {
    if (isDeleteError) {
      toast.error(translate("AI_CHAT/HISTORY/DELETE_ERROR"))
      isProcessingRef.current = false
    } else if (isDeleteSuccess) {
      setIsDeleteModalOpen(false)
      toast.success(translate("AI_CHAT/HISTORY/DELETE_SUCCESS"))
      isProcessingRef.current = false
    }
  }, [isDeleteSuccess, isDeleteError, setIsDeleteModalOpen, translate])

  const handleCloseDeleteModal = () => {
    setIsDeleteModalOpen(false)
  }

  const handleDelete = () => {
    if (isProcessingRef.current || isDeletePending) {
      return
    }
    isProcessingRef.current = true
    deleteConversation(conversationId)
  }

  return createPortal(
    <Modal
      ModalIcon={isDeletePending ? LoadingSpinner : DeleteIcon}
      descriptionText={translate("AI_CHAT/HISTORY/DELETE_DESCRIPTION")}
      primaryActionTitle={translate("AI_CHAT/HISTORY/DELETE")}
      secondaryActionTitle={translate("AI_CHAT/HISTORY/CANCEL")}
      onClose={handleCloseDeleteModal}
      onPrimaryAction={handleDelete}
      onSecondaryAction={handleCloseDeleteModal}
      modalTestId={dataTestId}
      primaryActionButtonTestId="delete-conversation"
      secondaryActionButtonTestId="cancel-delete-conversation"
      isPrimaryActionDisabled={isDeletePending}
      showSidebarLeft
    />,
    document.body,
  )
}
